import { shippingTargetDetail } from '@/interfaces/plan/shipping';
import ShippingTemplate from '@/templates/ShippingTemplate';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import * as _ from 'lodash';
import * as df from 'date-fns';
import { colorList } from '@/styles/colorList';
import {
  backdropAtom,
  backdropTextAtom,
  basicInfoAtom,
  DEFAULT_ZOOM,
  destDetailAtom,
  mapZoomLevelAtom,
  resultDestListAtom,
  selectedCarListAtom,
  shippingListAtom,
  targetShippingSeqAtom,
  timeLineChgArrAtom,
  timeLineOrgArrAtom,
} from '@/commons/constants';
import { nearXFiveZeroMin, timeStringtoSecond } from '@/commons/utils';
import { Backdrop, CircularProgress, Typography } from '@mui/material';

import { sidAtom } from './PlanContainer';
import { Dest, ShippingDetail } from '@/types/result';
import { useResetAtom } from 'jotai/utils';

export default function ShippingContainer({ mode }: { mode: string }) {
  const targetShipping = useAtomValue(targetShippingSeqAtom);
  const { data, isRefetching } = useAtomValue(shippingTargetDetail);
  const setBasicInfo = useSetAtom(basicInfoAtom);
  const setShippingList = useSetAtom(shippingListAtom);
  const setSelectedCarList = useSetAtom(selectedCarListAtom);
  const setDestDetail = useSetAtom(destDetailAtom);
  const setMapZoomLevel = useSetAtom(mapZoomLevelAtom);
  const setResultDestList = useSetAtom(resultDestListAtom);
  const [backdrop, setBackDrop] = useAtom(backdropAtom);
  const backdropText = useAtomValue(backdropTextAtom);
  const sid = useAtomValue(sidAtom);
  const resetBasicInfo = useResetAtom(basicInfoAtom);
  const resetShippingList = useResetAtom(shippingListAtom);
  const resetSelectedCarList = useResetAtom(selectedCarListAtom);
  const resetDestDetail = useResetAtom(destDetailAtom);
  const resetOriginArray = useResetAtom(timeLineOrgArrAtom);
  const resetCopyArray = useResetAtom(timeLineChgArrAtom);

  useEffect(() => {
    if (targetShipping) {
      setBackDrop(true);
    }
    setMapZoomLevel(DEFAULT_ZOOM);
  }, [targetShipping]);

  useEffect(() => {
    if (data && data.returnCode === 'OK') {
      resetDestDetail();
      resetBasicInfo();
      resetSelectedCarList();
      resetShippingList();
      resetCopyArray();
      resetOriginArray();
      setTimeout(() => createShippingList(data), 300);
    } else if (data) {
      alert('배차결과를 가져오는데 실패하였습니다.\n다시 시도해주세요');
      setBackDrop(false);
    }
  }, [data, isRefetching]);

  const createShippingList = (target: any) => {
    setShippingList([]);
    setSelectedCarList([]);
    setDestDetail(undefined);
    if (target && target.routeList) {
      let colIndex = -1;
      const temp: ShippingDetail[] = _.map(target.routeList, (item: any, index: number) => {
        const car: any = _.pick(item, [
          'car_seq',
          'name',
          'start_time',
          'end_time',
          'lunch_time',
          'start_info',
          'end_info',
          'region',
          'min_volume',
          'max_volume',
        ]);
        const route: naver.maps.LatLng[] = item.route?.route.map((rItem: any, idx: number) => {
          return { lat: rItem.gpsLat, lng: rItem.gpsLng };
        });
        const reverse: naver.maps.LatLng[] = item.route?.return.map((rItem: any, idx: number) => {
          return { lat: rItem.gpsLat, lng: rItem.gpsLng };
        });
        const orderSheetList: number[] = [];
        const destList: Dest[] = [];
        _.each(item.dest_list, (v, index: number) => {
          _.each(v.orderInfo, (vl) => orderSheetList.push(vl));
          destList.push({
            ...v,
            delivery_no: index + 1,
            coord: new naver.maps.LatLng({ lat: v.lat, lng: v.lng }),
          });
        });
        let lastPoint = null;
        if (car.end_info.end_type !== 1) {
          const distance = item.tmap?.lastPoint?.distance;
          let arrivalTime = '';
          if (item.vrp) {
            const retDur = timeStringtoSecond(item.vrp.returnDuration + ':00');
            const dwell = destList[destList.length - 1].dwellTime.value;
            const destLastTime = destList[destList.length - 1].arrivalTime;
            arrivalTime = df.format(
              df.add(df.parse(destLastTime, 'HH:mm', new Date()), {
                seconds: retDur + dwell,
              }),
              'HH:mm',
            );
          } else {
            arrivalTime = item.tmap?.lastPoint?.arrivalTime;
          }
          lastPoint = {
            distance,
            arrivalTime,
          };
        }
        colIndex++;
        colIndex = colIndex < 20 ? colIndex : colIndex - 20;
        return {
          car,
          destList,
          route: route ?? [],
          reverse: reverse ?? [],
          color: colorList[colIndex],
          deliverCount: destList.length,
          distance: Number(item.tmap?.seq_total_distance ?? 0),
          time: timeStringtoSecond(
            item.vrp?.totalDuration
              ? item.vrp.totalDuration
              : item.tmap?.seq_total_duration
                ? item.tmap?.seq_total_duration + ':00'
                : '00:00:00',
          ),
          deliverStartTime: destList.length > 0 ? destList[0].arrivalTime + ':00' : null,
          deliverEndTime: destList.length > 0 ? destList[destList.length - 1]?.arrivalTime + ':00' : null,
          minDwellTime: _.min(_.map(destList, 'dwellTime.value')),
          maxDwellTime: _.max(_.map(destList, 'dwellTime.value')),
          orderSheetList,
          lastPoint,
        };
      });
      if (temp && temp.length > 0) {
        const deliverStartTime = _.min(
          _.map(
            temp, // _.filter(temp, (v) => v.deliverStartTime !== null),
            'deliverStartTime',
          ),
        );
        const deliverEndTime = _.max(
          _.map(
            temp, // _.filter(temp, (v) => v.deliverEndTime !== null),
            'deliverEndTime',
          ),
        );
        const minDwellTime = _.min(_.map(temp, 'minDwellTime'));
        const maxDwellTime = _.max(_.map(temp, 'maxDwellTime'));
        const startHour = df.add(nearXFiveZeroMin(df.parse(deliverStartTime, 'HH:mm:ss', new Date())), {
          minutes: -15,
        });
        const endHour = df.add(
          nearXFiveZeroMin(df.add(df.parse(deliverEndTime, 'HH:mm:ss', new Date()), { seconds: maxDwellTime })),
          { minutes: 15 },
        );
        const timeLine = {
          startHour,
          endHour,
          timelineLength: df.differenceInMinutes(endHour, startHour),
        };
        const basicInfo = {
          carCount: _.filter(temp, (o) => o.destList.length > 0).length,
          destCount: _.sumBy(temp, function (o) {
            return o.destList.length;
          }),
          orderCount: _.sumBy(temp, 'orderCount'),
          totalDistance: _.sumBy(temp, 'distance'),
          totalTime: _.sumBy(temp, 'time'),
          deliverStartTime,
          timeLine,
          minDwellTime,
          maxDwellTime,
        };
        setBasicInfo(basicInfo);
        setShippingList(temp);
        setSelectedCarList(temp.map((item: ShippingDetail) => item.car.car_seq));
        setResultDestList(target.destList);
      }
    }
  };

  const handleFullClick = async () => {
    window.open(`/ai/shipping/f_result/${targetShipping}?sid=${encodeURIComponent(sid)}`);
  };

  return (
    <>
      <ShippingTemplate
        title={
          data
            ? (data.shipping.center_name ?? '') + ' ' + data.shipping.name + '   (배송일:' + data?.shipping.date + ')'
            : ''
        }
        onClick={mode !== 'full' && targetShipping ? handleFullClick : undefined}
      />
      <Backdrop
        open={backdrop}
        sx={(theme) => ({ color: '#fff', zIndex: 100000, display: 'flex', flexDirection: 'column' })}
      >
        <CircularProgress color="inherit" />
        {backdropText.length > 0 && <Typography variant="h5">{backdropText}</Typography>}
      </Backdrop>
    </>
  );
}
